import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import type { FixedAssetWithSchedule } from '@accounting/shared';
import { useDisposeFixedAsset, useFixedAsset, useRunDepreciation, useUpdateFixedAsset } from '../../hooks/useFixedAssets';
import { useAccounts } from '../../hooks/useAccounts';
import { AccountPicker } from '../../components/accounts/AccountPicker';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { ApiError } from '../../lib/api';
import { formatAccountingDate, formatCurrency, todayIso } from '../../lib/formatters';
import { assetStatusTag } from './fixed-assets.index';

export const Route = createFileRoute('/_auth/fixed-assets/$id')({
  component: FixedAssetDetail,
});

function FixedAssetDetail() {
  const { id } = Route.useParams();
  const companyId = useActiveCompanyId();
  const { data: asset, isLoading, error } = useFixedAsset(companyId, id);
  const { data: accounts = [] } = useAccounts(companyId);
  const depreciate = useRunDepreciation(companyId);
  const dispose = useDisposeFixedAsset(companyId);

  const [throughDate, setThroughDate] = useState(todayIso());
  const [disposeOpen, setDisposeOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  if (!companyId) return <div className="card" style={{ textAlign: 'center', padding: 32 }}><p className="muted">No active company.</p></div>;
  if (isLoading) return <p className="muted">Loading…</p>;
  if (error || !asset) return <p className="field-error">Failed to load asset.</p>;

  const depErr =
    depreciate.error instanceof ApiError
      ? (depreciate.error.body as { error?: string } | null)?.error ?? 'Depreciation failed'
      : depreciate.error?.message;

  const cashAccounts = accounts.filter((a) => a.isActive && a.allowDirectPosting && a.accountType === 'asset');

  return (
    <div>
      <Link to="/fixed-assets" style={{ fontSize: 13 }}>← Fixed Assets</Link>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '8px 0 16px', flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ fontSize: 24, margin: 0 }}>{asset.name} <span style={{ marginLeft: 8 }}>{assetStatusTag(asset)}</span></h1>
        {asset.status !== 'disposed' && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-secondary" onClick={() => setEditOpen(true)}>Edit</button>
            <button className="btn-danger" onClick={() => setDisposeOpen(true)}>Dispose</button>
          </div>
        )}
      </div>

      <div className="card" style={{ padding: 16, marginBottom: 16, maxWidth: 720 }}>
        <div className="form-grid" style={{ marginBottom: 0 }}>
          <span className="muted">In service</span><span>{formatAccountingDate(asset.inServiceDate)}</span>
          <span className="muted">Cost</span><span>{formatCurrency(asset.cost)}</span>
          <span className="muted">Salvage</span><span>{formatCurrency(asset.salvageValue)}</span>
          <span className="muted">Useful life</span><span>{asset.usefulLifeMonths} months (straight-line, {asset.convention === 'mid_month' ? 'mid-month convention' : 'full months'})</span>
          <span className="muted">Auto-post</span><span>{asset.autoPost ? 'On — each closed month posts nightly' : 'Off — post by hand below'}</span>
          <span className="muted">Accumulated depreciation</span><span>{formatCurrency(asset.accumulatedDepreciation)}</span>
          <span className="muted">Book value</span><strong>{formatCurrency(asset.bookValue)}</strong>
          {asset.lastDepreciatedThrough && (<><span className="muted">Depreciated through</span><span>{formatAccountingDate(asset.lastDepreciatedThrough)}</span></>)}
          {asset.disposedDate && (<><span className="muted">Disposed</span><span>{formatAccountingDate(asset.disposedDate)} (proceeds {formatCurrency(asset.disposalProceeds ?? '0')})</span></>)}
        </div>
      </div>

      {asset.status === 'active' && (
        <div className="card" style={{ padding: 16, marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <strong style={{ fontSize: 14 }}>Run depreciation through</strong>
          <input type="date" value={throughDate} onChange={(e) => setThroughDate(e.target.value)} />
          <button className="btn-primary" disabled={depreciate.isPending} onClick={() => depreciate.mutate({ id, throughDate })}>
            {depreciate.isPending ? 'Posting…' : 'Post depreciation'}
          </button>
          {depreciate.data && <span className="muted">Posted {depreciate.data.posted} month(s), {formatCurrency(depreciate.data.amount)}.</span>}
          {depErr && <span className="field-error">{depErr}</span>}
        </div>
      )}

      <div className="card" style={{ padding: 16 }}>
        <h2 style={{ fontSize: 15, marginTop: 0 }}>Depreciation schedule</h2>
        <table className="table">
          <thead>
            <tr>
              <th>Period</th>
              <th style={{ textAlign: 'right' }}>Depreciation</th>
              <th style={{ textAlign: 'right' }}>Accumulated</th>
              <th style={{ textAlign: 'right' }}>Book value</th>
              <th>Posted</th>
            </tr>
          </thead>
          <tbody>
            {asset.schedule.map((p) => (
              <tr key={p.periodDate}>
                <td>{formatAccountingDate(p.periodDate)}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(p.amount)}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(p.accumulated)}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(p.bookValue)}</td>
                <td>{p.posted ? <span className="tag ok">posted</span> : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {disposeOpen && <DisposeModal companyId={companyId} assetId={id} bookValue={asset.bookValue} accounts={accounts} cashAccounts={cashAccounts} onClose={() => setDisposeOpen(false)} disposeMut={dispose} />}
      {editOpen && <EditAssetModal companyId={companyId} asset={asset} accounts={accounts} onClose={() => setEditOpen(false)} />}
    </div>
  );
}

function EditAssetModal({
  companyId,
  asset,
  accounts,
  onClose,
}: {
  companyId: string;
  asset: FixedAssetWithSchedule;
  accounts: import('@accounting/shared').Account[];
  onClose: () => void;
}) {
  const update = useUpdateFixedAsset(companyId);

  const [name, setName] = useState(asset.name);
  const [description, setDescription] = useState(asset.description ?? '');
  const [cost, setCost] = useState(asset.cost);
  const [salvage, setSalvage] = useState(asset.salvageValue);
  const [inServiceDate, setInServiceDate] = useState(asset.inServiceDate);
  const [usefulLifeMonths, setUsefulLifeMonths] = useState(String(asset.usefulLifeMonths));
  const [convention, setConvention] = useState<FixedAssetWithSchedule['convention']>(asset.convention);
  const [autoPost, setAutoPost] = useState(asset.autoPost);
  const [assetAccountId, setAssetAccountId] = useState(asset.assetAccountId);
  const [accumDepAccountId, setAccumDepAccountId] = useState(asset.accumDepAccountId);
  const [depExpenseAccountId, setDepExpenseAccountId] = useState(asset.depExpenseAccountId);
  const [override, setOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');

  // Track which fields have changed vs. current asset state.
  const nameChanged = name.trim() !== asset.name;
  const descChanged = (description || null) !== asset.description;
  const costChanged = cost !== asset.cost;
  const salvageChanged = salvage !== asset.salvageValue;
  const dateChanged = inServiceDate !== asset.inServiceDate;
  const lifeChanged = Number(usefulLifeMonths) !== asset.usefulLifeMonths;
  const conventionChanged = convention !== asset.convention;
  const autoPostChanged = autoPost !== asset.autoPost;
  const assetAcctChanged = assetAccountId !== asset.assetAccountId;
  const accumAcctChanged = accumDepAccountId !== asset.accumDepAccountId;
  const depExpAcctChanged = depExpenseAccountId !== asset.depExpenseAccountId;

  // "Schedule fields" — same list as the server. When any of these change AND
  // depreciation was already posted, override + reason are required.
  const scheduleChanged =
    costChanged || salvageChanged || dateChanged || lifeChanged || conventionChanged ||
    assetAcctChanged || accumAcctChanged || depExpAcctChanged;
  const hasPostedDep = !!asset.lastDepreciatedThrough;
  const needsOverride = scheduleChanged && hasPostedDep;

  const errorMessage =
    update.error instanceof ApiError
      ? (update.error.body as { error?: string } | null)?.error ?? 'Update failed'
      : update.error?.message;

  const canSubmit =
    (nameChanged || descChanged || scheduleChanged || autoPostChanged) &&
    Number(cost) > 0 &&
    Number(salvage) >= 0 &&
    Number(usefulLifeMonths) > 0 &&
    (!needsOverride || (override && overrideReason.trim().length > 0)) &&
    !update.isPending;

  const submit = () => {
    const input: import('@accounting/shared').UpdateFixedAssetInput = {};
    if (nameChanged) input.name = name.trim();
    if (descChanged) input.description = description.trim() || null;
    if (costChanged) input.cost = cost;
    if (salvageChanged) input.salvageValue = salvage;
    if (dateChanged) input.inServiceDate = inServiceDate;
    if (lifeChanged) input.usefulLifeMonths = Number(usefulLifeMonths);
    if (conventionChanged) input.convention = convention;
    if (autoPostChanged) input.autoPost = autoPost;
    if (assetAcctChanged) input.assetAccountId = assetAccountId;
    if (accumAcctChanged) input.accumDepAccountId = accumDepAccountId;
    if (depExpAcctChanged) input.depExpenseAccountId = depExpenseAccountId;
    if (needsOverride) {
      input.override = true;
      input.overrideReason = overrideReason.trim();
    }
    update.mutate({ id: asset.id, input }, { onSuccess: () => onClose() });
  };

  const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4, marginTop: 10 } as const;

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ fontSize: 18, marginTop: 0 }}>Edit asset</h2>

        {needsOverride && (
          <div
            className="card"
            style={{
              background: 'var(--tag-warn-bg)',
              color: 'var(--tag-warn-fg)',
              borderColor: 'var(--tag-warn-fg)',
              marginBottom: 12,
              fontSize: 13,
            }}
          >
            <strong>Schedule reset required.</strong> This asset has posted depreciation. Changing
            cost, salvage, useful life, in-service date, or any of the linked GL accounts will
            void every posted depreciation JE (owner override) and reset accumulated depreciation
            to zero so the schedule can be re-derived. The reconciliation records + hash chain are
            preserved, but any reconciliations that included those depreciation JEs will no
            longer tie out.
          </div>
        )}

        <label style={labelStyle}>Name</label>
        <input value={name} onChange={(e) => setName(e.target.value)} style={{ width: '100%' }} />

        <label style={labelStyle}>Description</label>
        <input value={description} onChange={(e) => setDescription(e.target.value)} style={{ width: '100%' }} />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={labelStyle}>Cost</label>
            <input inputMode="decimal" value={cost} onChange={(e) => setCost(e.target.value)} style={{ width: '100%', textAlign: 'right' }} />
          </div>
          <div>
            <label style={labelStyle}>Salvage</label>
            <input inputMode="decimal" value={salvage} onChange={(e) => setSalvage(e.target.value)} style={{ width: '100%', textAlign: 'right' }} />
          </div>
          <div>
            <label style={labelStyle}>In-service date</label>
            <input type="date" value={inServiceDate} onChange={(e) => setInServiceDate(e.target.value)} style={{ width: '100%' }} />
          </div>
          <div>
            <label style={labelStyle}>Useful life (months)</label>
            <input inputMode="numeric" value={usefulLifeMonths} onChange={(e) => setUsefulLifeMonths(e.target.value)} style={{ width: '100%', textAlign: 'right' }} />
          </div>
          <div>
            <label style={labelStyle}>Convention</label>
            <select value={convention} onChange={(e) => setConvention(e.target.value as FixedAssetWithSchedule['convention'])} style={{ width: '100%' }}>
              <option value="full_month">Full month (equipment, vehicles)</option>
              <option value="mid_month">Mid-month (buildings: 27.5 / 39 years)</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Auto-post</label>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 400, marginTop: 6 }}>
              <input type="checkbox" checked={autoPost} onChange={(e) => setAutoPost(e.target.checked)} />
              Post closed months nightly
            </label>
          </div>
        </div>

        <label style={labelStyle}>Asset account</label>
        <AccountPicker
          accounts={accounts}
          value={assetAccountId}
          onChange={setAssetAccountId}
          predicate={(a) => a.isActive && a.allowDirectPosting && a.accountType === 'asset'}
        />

        <label style={labelStyle}>Accumulated depreciation account</label>
        <AccountPicker
          accounts={accounts}
          value={accumDepAccountId}
          onChange={setAccumDepAccountId}
          predicate={(a) => a.isActive && a.allowDirectPosting && a.accountType === 'asset'}
        />

        <label style={labelStyle}>Depreciation expense account</label>
        <AccountPicker
          accounts={accounts}
          value={depExpenseAccountId}
          onChange={setDepExpenseAccountId}
          predicate={(a) => a.isActive && a.allowDirectPosting && a.accountType === 'expense'}
        />

        {needsOverride && (
          <>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12, fontWeight: 600 }}>
              <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} />
              I understand — reset the depreciation schedule.
            </label>
            <label style={labelStyle}>Override reason (required)</label>
            <input
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              placeholder="Why are you editing the schedule? (audited)"
              style={{ width: '100%' }}
              required
            />
          </>
        )}

        {errorMessage && <div className="field-error" style={{ marginTop: 10 }}>{errorMessage}</div>}

        <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={!canSubmit} onClick={submit}>
            {update.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DisposeModal({
  companyId, assetId, bookValue, accounts, cashAccounts, onClose, disposeMut,
}: {
  companyId: string;
  assetId: string;
  bookValue: string;
  accounts: import('@accounting/shared').Account[];
  cashAccounts: import('@accounting/shared').Account[];
  onClose: () => void;
  disposeMut: ReturnType<typeof useDisposeFixedAsset>;
}) {
  const [date, setDate] = useState(todayIso());
  const [proceeds, setProceeds] = useState('0');
  const [cashAccountId, setCashAccountId] = useState(cashAccounts[0]?.id ?? '');

  const errorMessage =
    disposeMut.error instanceof ApiError
      ? (disposeMut.error.body as { error?: string } | null)?.error ?? 'Disposal failed'
      : disposeMut.error?.message;

  const gain = (parseFloat(proceeds) || 0) - parseFloat(bookValue);
  const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4, marginTop: 10 } as const;

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ fontSize: 18, marginTop: 0 }}>Dispose asset</h2>
        <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
          Removes the asset and its accumulated depreciation, records the proceeds, and books the
          gain/loss. Book value: {formatCurrency(bookValue)}.
        </p>
        <label style={labelStyle}>Disposal date</label>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ width: '100%' }} />
        <label style={labelStyle}>Proceeds</label>
        <input inputMode="decimal" value={proceeds} onChange={(e) => setProceeds(e.target.value)} style={{ width: '100%', textAlign: 'right' }} />
        <label style={labelStyle}>Proceeds deposited to</label>
        <AccountPicker
          accounts={accounts}
          value={cashAccountId}
          onChange={setCashAccountId}
          predicate={(a) => a.isActive && a.allowDirectPosting && a.accountType === 'asset'}
        />
        <p className="muted" style={{ fontSize: 13, marginTop: 10 }}>
          {gain >= 0 ? 'Gain' : 'Loss'} on disposal: {formatCurrency(Math.abs(gain).toFixed(2))}
        </p>
        {errorMessage && <div className="field-error">{errorMessage}</div>}
        <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn-danger"
            disabled={disposeMut.isPending || !cashAccountId}
            onClick={() => disposeMut.mutate({ id: assetId, input: { date, proceeds: String(parseFloat(proceeds) || 0), cashAccountId } }, { onSuccess: () => onClose() })}
          >
            {disposeMut.isPending ? 'Disposing…' : 'Dispose'}
          </button>
        </div>
      </div>
    </div>
  );
}
